import { Link } from "@inertiajs/react";
import Layout from "../Layout";
import { CallToAction } from "@/components/marketing/CallToAction";
import { SiGithub } from "@icons-pack/react-simple-icons";
import {
  Cloud,
  FileCode,
  BarChart3,
  Search,
  TrendingDown,
  ShieldCheck,
  Eye,
  FileText,
  Shield,
  Settings,
  Server,
} from "lucide-react";
import { SlackMessagePreview } from "@/components/marketing/SlackMessagePreview";
import { ProductRecommendationsMockup } from "@/components/marketing/ProductRecommendationsMockup";
import {
  PullRequestPreview,
  InlineCode,
} from "@/components/marketing/PullRequestPreview";
import { DiffPreview, type DiffLine } from "@/components/marketing/DiffPreview";
import { WhatWeSupport } from "@/components/marketing/WhatWeSupport";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";

const SETUP_STEPS = [
  {
    icon: Cloud,
    title: "Connect AWS",
    description:
      "Add the Infralyst Terraform module to your infra repo. It creates a read-only IAM role so Infralyst can see your AWS resources and usage, but it never deploys changes in your accounts.",
  },
  {
    icon: SiGithub,
    title: "Connect GitHub",
    description:
      "Install the Infralyst GitHub App in your GitHub organization and choose which infrastructure repositories it can access. These are the repos where Infralyst will open Terraform pull requests.",
  },
  {
    icon: FileCode,
    title: "Map Terraform state",
    description:
      "Link your Terraform states to the right AWS accounts and repos. This keeps every recommendation tied back to the exact code that manages the resource.",
  },
] as const;

const ANALYSIS_STEPS = [
  {
    icon: BarChart3,
    title: "Collect data from AWS",
    description:
      "Infralyst pulls resource metadata and utilization metrics from CloudWatch across your accounts and regions. The more history available, the stronger the recommendation.",
  },
  {
    icon: Search,
    title: "Analyze what you are paying for",
    description:
      "Each resource type goes through its own set of checks. Infralyst looks for oversized instances, outdated configurations, and resources you are paying for but not using.",
  },
  {
    icon: TrendingDown,
    title: "Surface the highest-impact savings",
    description:
      "Recommendations are ranked by estimated monthly savings and grouped by account. Every recommendation includes the data behind it so you can verify before acting.",
  },
  {
    icon: ShieldCheck,
    title: "Conservative by design",
    description:
      "Every check is deterministic. Fixed thresholds, no machine learning, no AI. When data is sparse, noisy, or shows recurring spikes, Infralyst skips the recommendation rather than guessing. AI is only used later, to generate the Terraform code change.",
  },
] as const;

const RECOMMENDATIONS = [
  {
    name: "api-prod-01",
    subtext: "us-east-1 · 72 days",
    current: "m5.2xlarge",
    recommended: "m5.xlarge",
    cpuAvg: "4.2%",
    cpuPct: "18%",
    memAvg: "11%",
    memPct: "24%",
    savings: "$47/mo",
  },
  {
    name: "worker-batch-02",
    subtext: "us-east-1 · 52 days",
    current: "c5.xlarge",
    recommended: "c5.large",
    cpuAvg: "8.1%",
    cpuPct: "22%",
    memAvg: "12%",
    memPct: "28%",
    savings: "$23/mo",
  },
  {
    name: "cache-redis-03",
    subtext: "us-east-1 · 45 days",
    current: "r5.large",
    recommended: "r5.medium",
    cpuAvg: "6.8%",
    cpuPct: "19%",
    memAvg: "9%",
    memPct: "21%",
    savings: "$18/mo",
  },
] as const;

const PR_BODY = (
  <>
    <p>
      EC2 instance <InlineCode>worker-batch-02</InlineCode> in{" "}
      <InlineCode>us-east-1</InlineCode> can be downsized from{" "}
      <InlineCode>c5.xlarge</InlineCode> to <InlineCode>c5.large</InlineCode>,
      saving an estimated $23/mo.
    </p>

    <div className="mb-4 pl-3 border-l-4 border-amber-400 text-sm text-amber-700 dark:text-amber-400">
      This change is not zero downtime. Plan for a maintenance window.
    </div>

    <table className="w-full text-sm border-collapse mb-4">
      <thead>
        <tr className="border-b border-foreground/20">
          <th className="text-left py-1 pr-4">Metric</th>
          <th className="text-left py-1 pr-4">Avg</th>
          <th className="text-left py-1">P99.5</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-foreground/10">
          <td className="py-1 pr-4">CPU</td>
          <td className="py-1 pr-4">8.1%</td>
          <td className="py-1">22%</td>
        </tr>
        <tr>
          <td className="py-1 pr-4">Memory</td>
          <td className="py-1 pr-4">12%</td>
          <td className="py-1">28%</td>
        </tr>
      </tbody>
    </table>

    <p className="text-sm text-foreground/70">Lookback: 52 days</p>
  </>
);

const DIFF_LINES: DiffLine[] = [
  {
    type: "hunk",
    content:
      '@@ -42,8 +42,8 @@ resource "aws_iam_instance_profile" "cloudwatch_agent_profile" {',
  },
  { type: "context", content: "", oldLineNumber: 42, newLineNumber: 42 },
  {
    type: "context",
    content: 'resource "aws_instance" "worker_batch_02" {',
    oldLineNumber: 43,
    newLineNumber: 43,
  },
  {
    type: "context",
    content: "  ami           = data.aws_ami.amazon_linux.id",
    oldLineNumber: 44,
    newLineNumber: 44,
  },
  {
    type: "removed",
    content: '  instance_type = "c5.xlarge"',
    oldLineNumber: 45,
  },
  {
    type: "added",
    content: '  instance_type = "c5.large"',
    newLineNumber: 45,
  },
  {
    type: "context",
    content:
      "  iam_instance_profile = aws_iam_instance_profile.cloudwatch_agent_profile.name",
    oldLineNumber: 46,
    newLineNumber: 46,
  },
  {
    type: "context",
    content: "  tags = {",
    oldLineNumber: 47,
    newLineNumber: 47,
  },
  {
    type: "context",
    content: '    Name = "worker-batch-02"',
    oldLineNumber: 48,
    newLineNumber: 48,
  },
  { type: "context", content: "  }", oldLineNumber: 49, newLineNumber: 49 },
  { type: "context", content: "}", oldLineNumber: 50, newLineNumber: 50 },
];

const SAFETY_RAILS = [
  {
    icon: Eye,
    title: "Read-only access",
    description:
      "Infralyst connects via read-only IAM roles and a GitHub App. The only writes we perform are creating branches and pull requests.",
  },
  {
    icon: FileText,
    title: "No hidden automation",
    description:
      "Every change is a Terraform diff in one of your repos. Nothing runs in your AWS accounts. You review and deploy using your existing pipeline.",
  },
  {
    icon: Shield,
    title: "Conservative defaults",
    description:
      "If data is sparse, noisy, or shows recurring spikes, Infralyst skips the recommendation. When a change would require downtime (resizing a standalone EC2 instance, for example), Infralyst flags it in the pull request so your team can plan a maintenance window.",
  },
  {
    icon: Settings,
    title: "Per-resource control",
    description:
      "Dismiss individual recommendations or exclude specific resources so they are not suggested again. Every action is available from the dashboard and from Slack.",
  },
] as const;

function HowItWorksHero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          How <span className="text-primary">Infralyst</span> works
        </h1>
        <p className="max-w-2xl mx-auto mb-4 text-xl leading-relaxed text-surface-foreground/80">
          From AWS usage data to Terraform pull requests that reduce your AWS
          costs, inside your existing workflow.
        </p>
        <p className="max-w-2xl mx-auto text-surface-foreground/60">
          See how Infralyst connects to your cloud, analyzes usage, and prepares
          Terraform changes that your team reviews and merges.
        </p>
      </div>
    </section>
  );
}

function SetupSteps(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          1. Connect Infralyst to your infrastructure
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          A one-time setup that usually takes less than an hour.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SETUP_STEPS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-lg font-bold mb-3">{title}</h3>
              <p className="text-surface-foreground/80 text-sm leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-surface-foreground/60 mt-8">
          Once setup is complete, Infralyst runs in the background and keeps
          watching for new cost-saving opportunities.
        </p>
      </div>
    </section>
  );
}

function HowItFindsChanges(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          2. How Infralyst finds savings
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          Deterministic analysis. No AI, no guesswork.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {ANALYSIS_STEPS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-surface-foreground/80 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewRecommendations(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          3. Review recommendations
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          Ranked by estimated monthly savings and grouped by account. Dismiss
          what does not fit, generate a PR for the rest.
        </p>

        <ProductRecommendationsMockup
          title="EC2 Recommendations"
          icon={Server}
          recommendations={RECOMMENDATIONS}
          footerText="Analyzed 47 instances across 3 regions"
        />
      </div>
    </section>
  );
}

function GenerateAndMerge(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          4. Generate a PR, review, and merge
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          Click Generate PR. An AI agent reads your repo, locates the right
          Terraform files, writes the change, and opens a pull request. Your
          team reviews and merges like any other infrastructure change.
        </p>

        <PullRequestPreview
          title="Downsize EC2 instance worker-batch-02: c5.xlarge → c5.large (-$23/mo)"
          author="infralyst"
          status="open"
          body={PR_BODY}
          filesChangedContent={
            <DiffPreview filename="workers.tf" lines={DIFF_LINES} />
          }
        />
      </div>
    </section>
  );
}

function SlackIntegration(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          5. Optionally connect Slack
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          Map AWS accounts or GitHub repos to Slack channels so each team sees
          what is relevant to them. Generate a PR or dismiss a recommendation
          without leaving Slack.
        </p>

        <div className="max-w-xl mx-auto overflow-x-auto">
          <div className="min-w-[500px]">
            <SlackMessagePreview>
              <p className="font-bold text-[15px] leading-snug mb-5">
                New EC2 savings recommendation · Save $23.00/mo
              </p>

              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Resource</p>
                  <p className="font-mono text-sm text-foreground/80">
                    worker-batch-02 (us-east-1)
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Lookback</p>
                  <p className="font-mono text-sm text-foreground/80">52d</p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU Avg</p>
                  <p className="font-mono text-sm text-foreground/80">8.1%</p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">22%</p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem Avg</p>
                  <p className="font-mono text-sm text-foreground/80">12%</p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">28%</p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Current</p>
                  <p className="font-mono text-sm text-surface-foreground/60">
                    c5.xlarge
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Recommended</p>
                  <p className="font-mono text-sm text-primary font-medium">
                    c5.large
                  </p>
                </div>
              </div>

              <p className="text-xs text-primary mb-3">View on Infralyst</p>

              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
                  Generate PR
                </span>
                <span className="text-xs font-bold px-3 py-1.5 rounded-md border border-foreground/25 text-foreground/70">
                  Dismiss
                </span>
              </div>
            </SlackMessagePreview>
          </div>
        </div>
      </div>
    </section>
  );
}

function ControlsAndSafetyRails(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Controls and safety rails
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12">
          Built to fit teams that already treat infrastructure as code.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {SAFETY_RAILS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-none">
                  <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{title}</h3>
                  <p className="text-surface-foreground/80 text-sm leading-relaxed">
                    {description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-surface-foreground/60 mt-8">
          For more on access and data handling, see{" "}
          <Link href="/security" className="text-primary hover:underline">
            Security at Infralyst
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

const HOW_IT_WORKS_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "What's the difference between AWS Compute Optimizer and a Terraform-native rightsizing tool?",
    answer:
      "AWS Compute Optimizer flags over-provisioned instances but stops at a suggestion. It doesn't know about your Terraform layout, so it can't generate code changes. Infralyst validates each recommendation through deterministic quality gates, then generates a Terraform PR with the exact code change. Your team reviews and merges using your existing CI/CD pipeline.",
  },
  {
    question: "What's the difference between automated and manual AWS rightsizing?",
    answer:
      "Manual rightsizing means pulling CloudWatch data yourself, cross-referencing pricing, deciding on an instance type, and editing Terraform files by hand, per resource, per region. Infralyst monitors utilization across accounts and regions continuously, surfaces recommendations ranked by savings, and generates PRs with an AI agent that reads your actual repo structure.",
  },
  {
    question: "How long does it take to set up Infralyst?",
    answer:
      "Most teams finish setup in under an hour. You add the Infralyst Terraform module for a read-only IAM role, install the GitHub App, and map your Terraform state files. Once connected, Infralyst runs in the background and starts surfacing recommendations as soon as it has at least 30 days of CloudWatch data.",
  },
  {
    question: "How does the Terraform PR generation work?",
    answer:
      "When you click Generate PR, an AI agent reads your infrastructure repository, locates the correct Terraform files and resource blocks, writes the instance type change, and opens a pull request. The PR body includes the utilization data, estimated savings, and a downtime warning when applicable. Your team reviews and merges it like any other code change.",
  },
  {
    question: "What AWS resource types does Infralyst support?",
    answer:
      "Infralyst supports standalone EC2 instances, Auto Scaling Groups (including ECS-managed ASGs), ECS Fargate services, and EBS volumes (gp2 to gp3 migration). Each resource type has dedicated analysis gates and thresholds based on its scaling model. Infralyst scans all enabled regions in your connected accounts automatically.",
  },
  {
    question: "Is it safe to let a tool generate Terraform changes for production infrastructure?",
    answer:
      "Yes, when the tool generates code your team reviews rather than applying changes directly. Infralyst connects via read-only IAM roles and cannot modify your AWS accounts. Every recommendation passes deterministic quality gates (CPU and memory P99.5 below 40%, seasonal spike checks over up to 365 days). You get a Terraform PR that goes through your existing review and deployment process.",
  },
  {
    question: "What happens if Infralyst doesn't have enough data for a resource?",
    answer:
      "Infralyst requires at least 30 days of CloudWatch history and a minimum data point threshold before making a recommendation. If data is sparse, noisy, or shows recurring utilization spikes, Infralyst skips the resource entirely. It would rather miss a savings opportunity than suggest a change that could cause problems.",
  },
  {
    question: "What happens if a rightsizing change causes a problem after merging?",
    answer:
      "Every change is a standard Terraform pull request in your repo. If you've already merged and applied a PR and need to roll back, revert the PR in GitHub and run terraform apply. Infralyst never merges or applies changes on your behalf, so your team stays in full control of what reaches production.",
  },
  {
    question: "Can I use Infralyst from Slack instead of the dashboard?",
    answer:
      "Yes. Infralyst sends recommendation notifications to mapped Slack channels, where you can generate a PR or dismiss directly from the message. You can map specific AWS accounts or GitHub repos to different channels so each team only sees what's relevant to them. The dashboard and Slack stay in sync.",
  },
  {
    question: "How does Infralyst handle multiple AWS accounts and regions?",
    answer:
      "One workspace can connect unlimited AWS accounts. Infralyst scans all enabled regions in each account automatically and processes each region independently. If one region has an issue, it doesn't affect analysis in other regions. Recommendations are organized by account and region so your team can prioritize by environment.",
  },
] as const;

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="How It Works"
      description="Learn how Infralyst connects to your AWS accounts, analyzes utilization data, and generates Terraform pull requests for AWS cost optimization."
    >
      <HowItWorksHero />
      <SetupSteps />
      <HowItFindsChanges />
      <ReviewRecommendations />
      <GenerateAndMerge />
      <SlackIntegration />
      <WhatWeSupport />
      <ControlsAndSafetyRails />
      <FAQ
        items={HOW_IT_WORKS_FAQ_ITEMS}
        title="How It Works FAQ"
        highlightWord="Works"
        className="py-16 px-4"
      />
      <CallToAction
        heading="Ready to try Infralyst on your own AWS accounts?"
        subheading="Create a workspace, connect AWS and GitHub, and use your 3 free Terraform PRs on real infrastructure. You review and merge every change."
      />
    </Layout>
  );
}
